// Maps the backend's raw `branding_config` (delivered by every /join response
// and GET /api/tenants/:id/branding, BACKEND-GUIDE §1/§4.2) into the app's
// BrandingConfig. Pure — no React, no network.

import type { BrandingConfig } from './types';
import { DEFAULT_BRANDING } from './constants';

/** The shape the backend delivers. Keys are snake_case per the wire contract. */
export interface BackendBrandingConfig {
  name?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
}

/**
 * Build a BrandingConfig from a tenant id + its raw branding_config. Falls back
 * to the app default for any missing/invalid field, so a partial config still
 * renders a coherent, branded surface.
 *
 * NOTE — the colour tint is intentionally NOT derived from `primary_color`.
 * NSI Meet is a single-brand product: the room, the forms and every guest
 * surface must read in the design-system's NSI blue (`--primary`, globals.css),
 * never an arbitrary tenant colour piped from the backend — which was tinting
 * whole screens (the post-call notes form most visibly) purple. Forcing
 * `tint: null` here is the single source of truth for "no off-brand colour
 * anywhere"; logo/name still flow through so alt text stays correct.
 */
export function brandingFromConfig(
  tenantId: string | null | undefined,
  config: BackendBrandingConfig | null | undefined,
): BrandingConfig {
  return {
    tenantId: tenantId || DEFAULT_BRANDING.tenantId,
    name: config?.name?.trim() || DEFAULT_BRANDING.name,
    logoUrl: config?.logo_url?.trim() || DEFAULT_BRANDING.logoUrl,
    tint: null,
  };
}
